import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Layout } from '../components/layout/Layout'
import { useAuth } from '../auth/AuthContext'
import {
  OfflineRecording,
  saveRecording,
  listRecordings,
  deleteRecording
} from '../lib/offlineRecordings'
import { uploadRecording, syncPendingRecordings, cancelUpload, resetStaleUploads } from '../lib/recordingUploader'

const SIZE_WARN_BYTES = 24 * 1024 * 1024 // 24MB超はサーバー側のWhisper 25MB制限で失敗する可能性

// iOS SafariはWebMを録音できないため audio/mp4 を優先する
const MIME_CANDIDATES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']

function pickMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  for (const t of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(t)) return t
  }
  return ''
}

function defaultTitle(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `会議録音 ${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatDuration(sec: number): string {
  const s = Math.floor(sec)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const ss = s % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`
}

function formatSize(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

function statusLabel(status: OfflineRecording['status']): string {
  switch (status) {
    case 'pending': return 'アップロード待ち'
    case 'uploading': return 'アップロード中...'
    case 'done': return 'アップロード済み'
    case 'error': return 'エラー'
  }
}

export function RecordPage() {
  const { user, firebaseUser } = useAuth()
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [recState, setRecState] = useState<'idle' | 'recording' | 'paused'>('idle')
  const [elapsedSec, setElapsedSec] = useState(0)
  const [title, setTitle] = useState(defaultTitle())
  const [recordings, setRecordings] = useState<OfflineRecording[]>([])
  const [micError, setMicError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<number | null>(null)
  const elapsedRef = useRef(0)
  const titleRef = useRef(title)
  const wakeLockRef = useRef<any>(null)
  titleRef.current = title

  const refreshList = useCallback(async () => {
    setRecordings(await listRecordings())
  }, [])

  const onRecordingChange = useCallback((rec: OfflineRecording) => {
    setRecordings(prev => prev.map(r => (r.id === rec.id ? rec : r)))
  }, [])

  const triggerSync = useCallback(() => {
    if (!navigator.onLine || !firebaseUser || !user?.orgId) return
    syncPendingRecordings({ firebaseUser, orgId: user.orgId }, onRecordingChange)
      .then(refreshList)
      .catch(err => console.error('Sync failed:', err))
  }, [firebaseUser, user?.orgId, onRecordingChange, refreshList])

  // 初期ロード + online/offline監視
  useEffect(() => {
    // 前セッションで「アップロード中」のまま取り残された録音を待機状態へ戻してから一覧表示
    resetStaleUploads().then(refreshList)
    const onOnline = () => { setIsOnline(true); triggerSync() }
    const onOffline = () => setIsOnline(false)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [refreshList, triggerSync])

  // ページ表示時（オンラインなら）自動同期
  useEffect(() => { triggerSync() }, [triggerSync])

  // 画面スリープ防止（ベストエフォート。iOS Safari 16.4+で対応）
  const acquireWakeLock = useCallback(async () => {
    try {
      const nav = navigator as any
      if (nav.wakeLock?.request) {
        wakeLockRef.current = await nav.wakeLock.request('screen')
      }
    } catch {
      // 非対応・省電力モードなどでは無視
    }
  }, [])

  const releaseWakeLock = useCallback(() => {
    try { wakeLockRef.current?.release?.() } catch { /* noop */ }
    wakeLockRef.current = null
  }, [])

  useEffect(() => {
    const onVisibility = () => {
      // wakeLockはタブ非表示で自動解放されるため、録音中なら再取得する
      if (document.visibilityState === 'visible' && mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        acquireWakeLock()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [acquireWakeLock])

  const startTimer = () => {
    if (timerRef.current !== null) return
    timerRef.current = window.setInterval(() => {
      elapsedRef.current += 1
      setElapsedSec(elapsedRef.current)
    }, 1000)
  }

  const stopTimer = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const startRecording = async () => {
    setMicError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream
      const mimeType = pickMimeType()
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      chunksRef.current = []
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      recorder.onstop = async () => {
        stopTimer()
        releaseWakeLock()
        stream.getTracks().forEach(t => t.stop())
        streamRef.current = null

        const type = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type })
        chunksRef.current = []

        const rec: OfflineRecording = {
          id: `rec_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          title: titleRef.current.trim() || defaultTitle(),
          blob,
          mimeType: type.split(';')[0],
          durationSec: elapsedRef.current,
          createdAt: Date.now(),
          status: 'pending'
        }
        await saveRecording(rec)
        elapsedRef.current = 0
        setElapsedSec(0)
        setTitle(defaultTitle())
        await refreshList()
        // オンラインなら即アップロード
        triggerSync()
      }
      recorder.start(10_000) // 10秒ごとにチャンク化してメモリ上に保持
      mediaRecorderRef.current = recorder
      elapsedRef.current = 0
      setElapsedSec(0)
      setRecState('recording')
      startTimer()
      acquireWakeLock()
    } catch (e) {
      console.error('Failed to start recording:', e)
      setMicError('マイクにアクセスできませんでした。ブラウザのマイク許可設定を確認してください。')
    }
  }

  const pauseSupported = typeof MediaRecorder !== 'undefined' && 'pause' in MediaRecorder.prototype

  const pauseRecording = () => {
    const r = mediaRecorderRef.current
    if (r && r.state === 'recording') {
      try { r.pause(); stopTimer(); setRecState('paused') } catch { /* pause非対応 */ }
    }
  }

  const resumeRecording = () => {
    const r = mediaRecorderRef.current
    if (r && r.state === 'paused') {
      try { r.resume(); startTimer(); setRecState('recording') } catch { /* noop */ }
    }
  }

  const stopRecording = () => {
    const r = mediaRecorderRef.current
    if (r && r.state !== 'inactive') {
      r.stop()
      mediaRecorderRef.current = null
      setRecState('idle')
    }
  }

  // アンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      stopTimer()
      releaseWakeLock()
      const r = mediaRecorderRef.current
      if (r && r.state !== 'inactive') {
        try { r.stop() } catch { /* noop */ }
      }
      streamRef.current?.getTracks().forEach(t => t.stop())
    }
  }, [releaseWakeLock])

  const handleManualUpload = async (id: string) => {
    if (!firebaseUser || !user?.orgId) return
    if (!navigator.onLine) {
      alert('オフラインのためアップロードできません。オンライン復帰後に自動アップロードされます。')
      return
    }
    await uploadRecording(id, { firebaseUser, orgId: user.orgId }, onRecordingChange)
    await refreshList()
  }

  // 録音データを端末へ取り出す
  // ネイティブアプリ: Filesystemに書き出して共有シート（「ファイルに保存」/AirDrop等）
  // ブラウザ: Web Share API → 通常ダウンロードの順でフォールバック
  const handleDownload = async (rec: { title: string; blob: Blob; mimeType: string }) => {
    const extMap: Record<string, string> = {
      'audio/mp4': 'm4a', 'audio/mpeg': 'mp3', 'audio/webm': 'webm',
      'audio/wav': 'wav', 'audio/x-wav': 'wav', 'audio/ogg': 'ogg', 'audio/aac': 'aac'
    }
    const ext = extMap[rec.mimeType] || 'm4a'
    const safeName = `${rec.title.replace(/[\\/:*?"<>|]/g, '_')}.${ext}`
    // iOSの共有シートは日本語・スペース入りファイル名のfile URLを解決できないことがあるためASCIIのみにする
    const d = new Date((rec as any).createdAt || Date.now())
    const pad2 = (n: number) => String(n).padStart(2, '0')
    const asciiName = `recording_${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}_${pad2(d.getHours())}${pad2(d.getMinutes())}.${ext}`

    // ネイティブ（Capacitor）: WKWebViewはWeb共有/ダウンロード非対応のためネイティブ共有を使う
    try {
      const { Capacitor } = await import('@capacitor/core')
      if (Capacitor.isNativePlatform()) {
        if (!Capacitor.isPluginAvailable('Share')) {
          alert('この機能にはアプリ本体の更新が必要です。XcodeからiPhoneへ再インストール（上書き）してください。録音データは消えません。')
          return
        }
        let step = 'base64変換'
        try {
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(String(reader.result).split(',')[1] || '')
            reader.onerror = () => reject(reader.error)
            reader.readAsDataURL(rec.blob)
          })
          const { Filesystem, Directory } = await import('@capacitor/filesystem')
          // iOSの共有シートはCache領域のファイルを解決できないことがあるためDocumentsへ書き出す
          step = 'ファイル書き込み'
          await Filesystem.writeFile({
            path: asciiName,
            data: base64,
            directory: Directory.Documents
          })
          step = 'サイズ確認'
          const stat = await Filesystem.stat({ path: asciiName, directory: Directory.Documents })
          step = `共有(サイズ${Math.round((Number(stat.size) || 0) / 1024 / 1024)}MB)`
          const { uri } = await Filesystem.getUri({ path: asciiName, directory: Directory.Documents })
          const { Share } = await import('@capacitor/share')
          try {
            await Share.share({ title: rec.title, files: [uri] })
          } finally {
            // 共有後は一時ファイルを削除（端末容量の節約・ベストエフォート）
            Filesystem.deleteFile({ path: asciiName, directory: Directory.Documents }).catch(() => {})
          }
          return
        } catch (stepError) {
          const m = String((stepError as any)?.message || stepError)
          if (/cancel/i.test(m)) return
          alert(`録音データの取り出しに失敗しました (v4 / ${step}): ${m}`)
          return
        }
      }
    } catch (e) {
      // 共有シートのキャンセルは正常終了
      const msg = String((e as any)?.message || e)
      if (/cancel/i.test(msg)) return
      console.warn('native share failed:', e)
      alert('録音データの取り出しに失敗しました (v3): ' + msg)
      return
    }

    try {
      const file = new File([rec.blob], safeName, { type: rec.mimeType || 'audio/mp4' })
      if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: rec.title })
        return
      }
    } catch (e) {
      // 共有キャンセル等はフォールバックせず終了（AbortError）
      if ((e as any)?.name === 'AbortError') return
      console.warn('share failed, fallback to download:', e)
    }
    const url = URL.createObjectURL(rec.blob)
    const a = document.createElement('a')
    a.href = url
    a.download = safeName
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('この録音を削除しますか？')) return
    await deleteRecording(id)
    await refreshList()
  }

  // アップロードを途中停止（データは端末に残り、アップロード待ちに戻る）
  const handleCancelUpload = async (id: string) => {
    await cancelUpload(id)
    await refreshList()
  }

  const isRecording = recState !== 'idle'

  return (
    <Layout title="録音" showBackButton={true} backPath="/" showSidebar={true}>
      <div className="container-md w-full">
        {/* オフラインバッジ */}
        {!isOnline && (
          <div
            className="flex items-center gap-2 mb-4"
            style={{
              background: 'var(--color-warning-bg, #fef3c7)',
              color: 'var(--color-warning-text, #92400e)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-3) var(--space-4)',
              fontSize: 13,
              fontWeight: 500
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0 }}>
              <path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.58 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" />
            </svg>
            {isRecording
              ? 'オフラインで録音中 — オンライン復帰後に自動アップロードされます'
              : 'オフラインです — 録音はこの端末に保存され、オンライン復帰後に自動アップロードされます'}
          </div>
        )}

        {/* 録音カード */}
        <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8) var(--space-6)' }}>
          <div className="form-group" style={{ textAlign: 'left' }}>
            <label className="form-label">録音タイトル</label>
            <input
              type="text"
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例: 会議録音"
            />
          </div>

          <div style={{ fontSize: 40, fontVariantNumeric: 'tabular-nums', fontWeight: 600, margin: 'var(--space-4) 0' }}>
            {formatDuration(elapsedSec)}
          </div>

          {/* 大きな録音ボタン */}
          <button
            type="button"
            onClick={isRecording ? stopRecording : startRecording}
            aria-label={isRecording ? '録音を停止' : '録音を開始'}
            style={{
              width: 88,
              height: 88,
              borderRadius: '50%',
              border: 'none',
              cursor: 'pointer',
              background: isRecording ? '#dc2626' : 'var(--color-accent, #2563eb)',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: isRecording ? '0 0 0 8px rgba(220,38,38,0.15)' : '0 0 0 8px rgba(37,99,235,0.12)',
              transition: 'background .15s'
            }}
          >
            {isRecording ? (
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 30, height: 30 }}>
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 36, height: 36 }}>
                <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v1a7 7 0 0 1-14 0v-1M12 18v4" />
              </svg>
            )}
          </button>

          <div className="mt-4 flex items-center justify-center gap-2">
            {isRecording && pauseSupported && (
              recState === 'recording' ? (
                <button className="btn btn-outline btn-sm" onClick={pauseRecording}>一時停止</button>
              ) : (
                <button className="btn btn-outline btn-sm" onClick={resumeRecording}>再開</button>
              )
            )}
          </div>

          <p className="text-sm text-secondary mt-4">
            {isRecording
              ? (recState === 'paused' ? '一時停止中' : '録音中...')
              : '録音はオフラインでも利用できます。停止するとこの端末に保存され、オンライン時に自動でアップロード・文字起こしされます。'}
          </p>

          {micError && <p className="text-sm text-danger mt-2">{micError}</p>}
        </div>

        {/* 録音リスト */}
        <div className="card mt-4">
          <h3 className="text-md font-semibold text-primary mb-4">録音一覧</h3>
          {recordings.length === 0 ? (
            <p className="text-sm text-secondary">保存された録音はありません</p>
          ) : (
            <div className="flex flex-col" style={{ gap: 'var(--space-3)' }}>
              {recordings.map((rec) => (
                <div
                  key={rec.id}
                  className="flex items-center justify-between gap-3"
                  style={{ border: '1px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)' }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p className="text-base font-medium text-primary" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {rec.title}
                    </p>
                    <p className="text-sm text-tertiary">
                      {new Date(rec.createdAt).toLocaleString('ja-JP')} ・ {formatDuration(rec.durationSec)} ・ {formatSize(rec.blob.size)}
                    </p>
                    <p className="text-sm" style={{ color: rec.status === 'error' ? '#dc2626' : rec.status === 'done' ? '#16a34a' : 'var(--color-text-secondary, #6b7280)' }}>
                      {statusLabel(rec.status)}
                      {rec.error ? ` — ${rec.error}` : ''}
                    </p>
                    {rec.blob.size > SIZE_WARN_BYTES && rec.status !== 'done' && (
                      <p className="text-sm text-tertiary">
                        25MBを超える録音はサーバー側で自動的に圧縮・分割して文字起こしされます
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                    {rec.status === 'done' && rec.documentId && (
                      <Link to={`/documents/${rec.documentId}`} className="btn btn-outline btn-sm">議事録を開く</Link>
                    )}
                    {rec.status === 'pending' && (
                      <button className="btn btn-primary btn-sm" onClick={() => handleManualUpload(rec.id)} disabled={!isOnline}>
                        手動アップロード
                      </button>
                    )}
                    {rec.status === 'error' && (
                      <button className="btn btn-primary btn-sm" onClick={() => handleManualUpload(rec.id)} disabled={!isOnline}>
                        再試行
                      </button>
                    )}
                    {rec.status === 'uploading' && (
                      <>
                        <span className="spinner sm" />
                        <button className="btn btn-outline btn-sm" onClick={() => handleCancelUpload(rec.id)}>
                          停止
                        </button>
                      </>
                    )}
                    <button className="btn btn-outline btn-sm" onClick={() => handleDownload(rec)} title="録音データを端末に保存・共有">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                      </svg>
                    </button>
                    {rec.status !== 'uploading' && (
                      <button className="btn btn-outline btn-sm" onClick={() => handleDelete(rec.id)}>削除</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}
